import { z } from 'zod';

export const RunSettings = z.object({
  webResearch: z.boolean().default(false),
  planReview: z.enum(['off', 'human', 'manager']).default('off'),
  executionApproval: z.enum(['standard', 'auto']).default('standard'),
});
export type RunSettings = z.infer<typeof RunSettings>;
export const RunSettingsOverride = z.object({
  webResearch: z.boolean().optional(),
  planReview: z.enum(['off', 'human', 'manager']).optional(),
  executionApproval: z.enum(['standard', 'auto']).optional(),
});
export type RunSettingsOverride = z.infer<typeof RunSettingsOverride>;
export const RunPhase = z.enum(['planning', 'awaiting_review', 'reviewing', 'executing', 'completed']);
export type RunPhase = z.infer<typeof RunPhase>;
export const ResearchSource = z.object({
  url: z
    .string()
    .url()
    .max(2000)
    .refine((s) => /^https?:\/\//i.test(s), 'Expected HTTP(S)'),
  title: z.string().max(500),
  retrievedAt: z.string().datetime(),
});
export const PlanInput = z.object({
  title: z.string().min(1).max(200),
  objective: z.string().min(1).max(4000),
  steps: z.array(z.string().min(1).max(2000)).min(1).max(50),
  sources: z.array(ResearchSource).max(100).default([]),
  risks: z.array(z.string().max(2000)).max(50).default([]),
  breakingChanges: z.boolean().default(false),
  validation: z.array(z.string().min(1).max(2000)).min(1).max(50),
});
export type PlanInput = z.infer<typeof PlanInput>;
export const PlanReviewOutcome = z.object({
  outcome: z.enum(['approve', 'request_changes', 'escalate']),
  reasoning: z.string().min(1).max(4000),
  significantRisk: z.boolean(),
  uncertain: z.boolean(),
});
export type PlanReviewOutcome = z.infer<typeof PlanReviewOutcome>;
export function resolveRunSettings(defaults: unknown, overrides?: RunSettingsOverride | null): RunSettings {
  return RunSettings.parse({
    ...RunSettings.parse(defaults ?? {}),
    ...Object.fromEntries(Object.entries(overrides ?? {}).filter(([, value]) => value !== undefined)),
  });
}

const PLAN_REVIEW_STRICTNESS = { off: 0, manager: 1, human: 2 } as const;

/**
 * The fields of a per-run override that loosen the agent's own settings: Auto execution over Standard,
 * or a weaker plan review. Web research is an opt-in capability, not a safety control.
 * Loosening is reserved for people who may change the agent itself (`agent:update`).
 */
export function loosenedRunSettings(
  defaults: unknown,
  override?: RunSettingsOverride | null,
): ('executionApproval' | 'planReview')[] {
  const base = RunSettings.parse(defaults ?? {});
  const out: ('executionApproval' | 'planReview')[] = [];
  if (override?.executionApproval === 'auto' && base.executionApproval !== 'auto')
    out.push('executionApproval');
  if (
    override?.planReview &&
    PLAN_REVIEW_STRICTNESS[override.planReview] < PLAN_REVIEW_STRICTNESS[base.planReview]
  )
    out.push('planReview');
  return out;
}

export const RunExecutionLimits = z.object({
  /** Absent for delegated work: the assignee's own step limit applies (it does the work, not the delegator). */
  maxSteps: z.number().int().min(1).optional(),
  maxRunTokens: z.number().int().min(0),
  maxRunCostUsd: z.number().min(0),
  maxSubagentDepth: z.number().int().min(0),
  maxSubagents: z.number().int().min(0),
});
export type RunExecutionLimits = z.infer<typeof RunExecutionLimits>;
